import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  CheckRounded,
  ContentCopyOutlined,
  ErrorOutlineOutlined,
  EventOutlined,
  ListAltOutlined,
  LoginOutlined,
  LogoutOutlined,
  PendingActionsOutlined,
  TagOutlined,
  TaskAltOutlined,
  TextSnippetOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { pfExitsHeader } from "pages/PayRoll/PayRollHeaders";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Required, ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { RecordHeaderCard, StatCardGrid, StatutoryList, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { EXIT_STATUS_COLORS, formatDay } from "./pfReportsData";

// Exits (P10) and the PF Exit drawer (P10a): everyone who left in the
// month with the EPFO exit code from the mapping. The company marks each
// exit on the EPFO portal and then marks it done here. Exits never block
// the register or the ECR.

const ALL = "all";
const EXIT_CODES_PATH = "/admin/payroll/configuration/statutory-configuration?item=pf&tab=exit-codes";
const { indigo, orange, purple, green, blue } = detailAccentPalette;

const copy = (text) => {
  navigator.clipboard?.writeText(text);
  showToast("success", `Copied ${text}`);
};

const CopyValue = ({ value }) => (
  <Mui.Stack direction="row" spacing={0.5} alignItems="center" component="span">
    <span>{value}</span>
    <Mui.IconButton size="small" aria-label={`Copy ${value}`} onClick={() => copy(value)} sx={{ p: 0.25 }}>
      <ContentCopyOutlined sx={{ fontSize: 15 }} />
    </Mui.IconButton>
  </Mui.Stack>
);

const ExitPanel = ({ open, row, onClose, onMarkDone }) => {
  const history = useHistory();
  const [markedOn, setMarkedOn] = useState("");
  const [remarks, setRemarks] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    setMarkedOn(row.marked_on || moment().format("YYYY-MM-DD"));
    setRemarks(row.remarks || "");
    setShowErrors(false);
  }, [open, row]);

  if (!row) return null;
  const mapped = Boolean(row.epfo_code);
  const done = row.status === "Marked on portal";
  const dateError = !markedOn
    ? "Pick the date you marked it"
    : moment(markedOn).isBefore(moment(row.exit_date), "day")
      ? "Can't be before the date of exit"
      : moment(markedOn).isAfter(moment(), "day")
        ? "Can't be in the future"
        : "";

  const save = () => {
    setShowErrors(true);
    if (dateError) return;
    onMarkDone(row.id, { marked_on: markedOn, remarks: remarks.trim(), marked_by: "You" });
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="PF Exit"
      subtitle={`${row.name} · ${row.id} · ${row.code_label === "Main" ? "Main" : "Extension"} code`}
      bodySx={panelBodySx}
      footer={
        done ? (
          <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          </Mui.Stack>
        ) : (
          <PanelActionFooter onCancel={onClose} primaryLabel="Mark as done" primaryProps={{ onClick: save, disabled: !mapped }} />
        )
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(row.name)}
          title={row.name}
          chip={<RequestStatusChip status={row.status === "Fix mapping" ? "Need mapping" : row.status} colorCode={EXIT_STATUS_COLORS[row.status]} />}
          meta={`${row.id} · UAN ${row.uan} · ${row.department}`}
          tone={mapped ? (done ? "teal" : "warning") : "danger"}
        />
        {!mapped ? (
          <RequestInfoBanner
            tone="danger"
            title={`“${row.exit_reason}” has no EPFO code.`}
            body="Map it in Exit Codes first; then this exit can be marked done."
            action={<SecondaryButton onClick={() => history.push(EXIT_CODES_PATH)} sx={{ whiteSpace: "nowrap" }}>Open Exit Codes</SecondaryButton>}
          />
        ) : null}
        <RequestDetailsSection
          title="COPY TO THE EPFO PORTAL"
          fields={[
            { icon: TagOutlined, label: "UAN", value: <CopyValue value={row.uan} />, accent: purple },
            {
              icon: EventOutlined,
              label: "Date of exit",
              value: <CopyValue value={moment(row.exit_date).format("DD/MM/YYYY")} />,
              helper: "From the employee's exit record",
              accent: orange,
            },
            {
              icon: TextSnippetOutlined,
              label: "Reason for leaving",
              value: mapped ? <CopyValue value={row.epfo_label} /> : "Not mapped",
              helper: mapped ? `Exit reason “${row.reason_label}” → code ${row.epfo_code} (Exit Codes)` : undefined,
              accent: blue,
            },
          ]}
        />
        <RequestDetailsSection
          title="ON THE EPFO PORTAL"
          fields={[
            { icon: LoginOutlined, label: "1 · Log in", value: "EPFO employer portal", accent: green },
            { icon: LogoutOutlined, label: "2 · Member → Mark Exit", value: "Enter UAN, date of exit and reason", accent: green },
            { icon: TaskAltOutlined, label: "3 · Come back here", value: "Mark as done", accent: green },
          ]}
        />
        {done ? (
          <RequestDetailsSection
            title="MARKED"
            fields={[
              { icon: CheckRounded, label: "Marked on portal on", value: formatDay(row.marked_on), helper: row.marked_by ? `by ${row.marked_by}` : undefined, accent: indigo },
              ...(row.remarks ? [{ icon: TextSnippetOutlined, label: "Remarks", value: row.remarks, accent: indigo, multiline: true }] : []),
            ]}
          />
        ) : (
          <FormSection title="Mark as done">
            <Field wide label={<>Marked on portal on<Required /></>} error={showErrors ? dateError : undefined}>
              <DateField value={markedOn} onChange={setMarkedOn} maxDate={moment().format("YYYY-MM-DD")} disabled={!mapped} error={showErrors ? dateError : undefined} />
            </Field>
            <Field wide label="Remarks">
              <TextField value={remarks} onChange={setRemarks} disabled={!mapped} placeholder="Optional" />
            </Field>
          </FormSection>
        )}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const PfExitsTab = ({ exitRows, monthInfo, onMarkDone }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState(ALL);
  const [viewId, setViewId] = useState(null);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pf_exits_saved_views",
    fullHeaders: pfExitsHeader,
  });

  const filters = { toMark: "To mark", marked: "Marked on portal", mapping: "Fix mapping" };
  const rows = card === ALL ? exitRows : exitRows.filter((row) => row.status === filters[card]);
  const count = (status) => exitRows.filter((row) => row.status === status).length;
  const month = monthInfo.label.split(" ")[0];

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>{initialsOf(row.name)}</Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        );
      case "joined_pf":
      case "exit_date":
        return formatDay(row[key]);
      case "epfo_label":
        return <ToneChip label={row.epfo_label} tone={row.epfo_code ? "blue" : "red"} />;
      case "status":
        return <RequestStatusChip status={row.status} colorCode={EXIT_STATUS_COLORS[row.status]} />;
      default:
        return row[key] || "—";
    }
  };

  return (
    <>
      <StatCardGrid
        value={card}
        onChange={setCard}
        cards={[
          { key: ALL, title: `Left in ${month}`, value: exitRows.length, icon: ListAltOutlined, tone: "blue" },
          { key: "toMark", title: "To mark on portal", value: count("To mark"), icon: PendingActionsOutlined, tone: "amber" },
          { key: "marked", title: "Marked", value: count("Marked on portal"), icon: CheckRounded, tone: "green" },
          { key: "mapping", title: "Need mapping", value: count("Fix mapping"), icon: ErrorOutlineOutlined, tone: "red" },
        ]}
      />
      <StatutoryList
        title={`PF Exits ${monthInfo.label}`}
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="exits"
        searchPlaceholder="Search name, ID or UAN"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Payroll Month": monthInfo.label, "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
      />
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Mark each exit on the EPFO portal (Member → Mark Exit) using the date and code shown, then mark it done here. The employee can then withdraw or transfer their PF." />
      </Mui.Box>
      <ExitPanel
        open={Boolean(viewId)}
        row={exitRows.find((row) => row.id === viewId)}
        onClose={() => setViewId(null)}
        onMarkDone={(id, change) => {
          onMarkDone(id, change);
          showToast("success", "Exit marked as done.");
          setViewId(null);
        }}
      />
    </>
  );
};
